import React, { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import FormInput from '../../shared/components/ui/FormInput';
import { Pencil, Trash, Copy, Upload, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { getAllSalesUsersByProjectId } from '../services/api/implementation/impl';
import { useQuery } from '@tanstack/react-query';
import Loader from '../../shared/components/ui/Loader';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Button from '../../shared/components/ui/Button';
import NavigationSelector from '../../shared/components/ui/NavigationSelector';
import SelectedIcon from '../../shared/assets/svg/CorrectGreen.svg';
import Thumbnail from '../../shared/assets/templates/templateOne/thumbnail.png';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import LOGO from '../../shared/assets/svg/LogoWhite.svg';
import MobileIcon from '../../shared/assets/svg/MobIcon.svg';
import WebIcon from '../../shared/assets/svg/PCIcon.svg';
import { Phone, Mail, X } from 'lucide-react';
import Network from '../../shared/assets/svg/mobNetwrok.svg';
import { useMutation } from '@tanstack/react-query';
import {
  createCampaignTemplate,
  getCampaignTemplate,
  updateCampaignTemplate
} from '../services/api/implementation/impl';
import { uploadImage } from '../../shared/services/api/implementation/impl';
import Multiselect from 'multiselect-react-dropdown';
import { Controller } from 'react-hook-form';
import { useProjectsList } from '../../../contexts/ProjectsContext';

const CampaignTemplates = () => {
  const userId = Cookies.get('userId');
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const [isloading, setisLoading] = useState(false);
  const [webBannerImage, setWebBannerImage] = useState(null);
  const [phoneBannerImage, setPhoneBannerImage] = useState(null);
  const [webBannerFileName, setWebBannerFileName] = useState(null);
  const [phoneBannerFileName, setPhoneBannerFileName] = useState(null);
  const [webBannerUrl, setWebBannerUrl] = useState(null);
  const [phoneBannerUrl, setPhoneBannerUrl] = useState(null);
  const [uploadingWebBanner, setUploadingWebBanner] = useState(false);
  const [uploadingPhoneBanner, setUploadingPhoneBanner] = useState(false);
  const [userProposition, setUserProposition] = useState('User Selling Proposition');
  const [showMobileView, setShowMobileView] = useState(false);
  const [showMobileForm, setShowMobileForm] = useState(false);
  const [selectedColor, setSelectedColor] = useState('white');
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  const [currentTemplateIndex, setCurrentTemplateIndex] = useState(0);
  const [selectedSalesPersons, setSelectedSalesPersons] = useState([]);
  const navigate = useNavigate();
  const validationSchema = yup.object().shape({
    project: yup.string().required('Select Project')
  });
  const { collapsed } = useSidebar();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
    watch
  } = useForm({
    resolver: yupResolver(validationSchema)
  });

  const projectID = watch('project');

  // Track page view
  useEffect(() => {
    mixpanel.track('Campaign Templates Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  const {
    data: campaignTemplate,
    refetch: fetchCampaignTemplate,
    isLoading: templateLoading
  } = useQuery({
    queryKey: ['campaignTemplate', projectID],
    queryFn: () => getCampaignTemplate(projectID),
    enabled: !!projectID
  });

  // Fetch sales persons for the selected project
  const {
    data: salesPersons,
    refetch: fetchSalesPersons,
    isLoading: salesPersonsLoading
  } = useQuery({
    queryKey: ['salesPersons', projectID],
    queryFn: () => getAllSalesUsersByProjectId(projectID, 1, 100000),
    enabled: !!projectID
  });

  // Update loading state when projects or templates are loading
  useEffect(() => {
    setisLoading(isProjectsLoading || templateLoading || salesPersonsLoading);
  }, [isProjectsLoading, templateLoading, salesPersonsLoading]);

  useEffect(() => {
    if (campaignTemplate && campaignTemplate?.length > 0) {
      const template = campaignTemplate[0];

      // Prefill form fields
      setValue('template', template?.name);

      // Prefill state with existing data
      setUserProposition(template?.oneLiner || 'User Selling Proposition');

      // Prefill color selection
      setSelectedColor(template?.color || 'white');

      // Prefill sales persons if they exist
      if (template?.sales_persons && Array.isArray(template?.sales_persons)) {
        // Use the sales_persons data directly from the template
        setSelectedSalesPersons(template.sales_persons);
      } else {
        setSelectedSalesPersons([]);
      }

      // Prefill web banner data
      if (template?.webBanner_url && template?.webBanner_name) {
        setWebBannerUrl(template.webBanner_url);
        setWebBannerFileName(template.webBanner_name);
        setWebBannerImage(template.webBanner_url); // Use URL for preview
      } else {
        setWebBannerUrl(null);
        setWebBannerFileName(null);
        setWebBannerImage(null);
      }

      // Prefill phone banner data
      if (template?.phoneBanner_url && template?.phoneBanner_name) {
        setPhoneBannerUrl(template.phoneBanner_url);
        setPhoneBannerFileName(template.phoneBanner_name);
        setPhoneBannerImage(template.phoneBanner_url); // Use URL for preview
      } else {
        setPhoneBannerUrl(null);
        setPhoneBannerFileName(null);
        setPhoneBannerImage(null);
      }
    } else if (projectID && !templateLoading) {
      // Reset form when no template data found for the project
      setValue('template', 'default');
      setUserProposition('User Selling Proposition');
      setSelectedColor('white');
      setSelectedSalesPersons([]);

      // Reset web banner data
      setWebBannerUrl(null);
      setWebBannerFileName(null);
      setWebBannerImage(null);

      // Reset phone banner data
      setPhoneBannerUrl(null);
      setPhoneBannerFileName(null);
      setPhoneBannerImage(null);
    }
  }, [campaignTemplate, projectID, templateLoading, setValue]);

  const { mutate: handleUpdateTemplate } = useMutation({
    mutationFn: (payload) => updateCampaignTemplate(campaignTemplate?.[0]?.id, payload),
    onMutate: () => {
      setisLoading(true);
    },
    onSuccess: async (data) => {
      setisLoading(false);
      toast.success('Template updated successfully!');
      // Refetch template data after successful update
      await fetchCampaignTemplate();
    },
    onError: (err) => {
      setisLoading(false);
      console.error(`Error updating template`, err);
      toast.error(err?.response?.data?.message || err?.message || `Error updating template`);
    }
  });

  useEffect(() => {
    if (projects?.length > 0) {
      setCurrentProjectIndex(0);
      setValue('project', projects[0].id);
    }
  }, [projects, setValue]);

  // Template options
  const templateOptions = [
    {
      id: 'default',
      name: 'Template - Default',
      value: 'default'
    }
  ];

  const handleProjectSelect = useCallback(
    (project, index) => {
      setCurrentProjectIndex(index);
      setValue('project', project.id);
    },
    [setValue]
  );

  const handleTemplateSelect = useCallback(
    (template, index) => {
      setCurrentTemplateIndex(index);
      setValue('template', template.value);
    },
    [setValue]
  );

  const handleWebBannerUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Store file for preview
      const reader = new FileReader();
      reader.onload = (e) => setWebBannerImage(e.target.result);
      reader.readAsDataURL(file);
      setWebBannerFileName(file.name);

      // Upload to S3 and get URL
      try {
        setUploadingWebBanner(true);
        const formData = new FormData();
        formData.append('files', file);

        const result = await uploadImage(formData);
        const uploadedFile = result.data.files?.[0];

        if (uploadedFile) {
          setWebBannerUrl(uploadedFile.presignedUrl);
          // toast.success('Web banner uploaded successfully!');
        }
      } catch (error) {
        console.error('Error uploading web banner:', error);
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            'Error uploading web banner. Please try again.'
        );
      } finally {
        setUploadingWebBanner(false);
      }
    }
  };

  const handlePhoneBannerUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Store file for preview
      const reader = new FileReader();
      reader.onload = (e) => setPhoneBannerImage(e.target.result);
      reader.readAsDataURL(file);
      setPhoneBannerFileName(file.name);

      // Upload to S3 and get URL
      try {
        setUploadingPhoneBanner(true);
        const formData = new FormData();
        formData.append('files', file);

        const result = await uploadImage(formData);
        const uploadedFile = result.data.files?.[0];

        if (uploadedFile) {
          setPhoneBannerUrl(uploadedFile.presignedUrl);
          // toast.success('Phone banner uploaded successfully!');
        }
      } catch (error) {
        console.error('Error uploading phone banner:', error);
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            'Error uploading phone banner. Please try again.'
        );
      } finally {
        setUploadingPhoneBanner(false);
      }
    }
  };

  const removeWebBanner = () => {
    setWebBannerImage(null);
    setWebBannerFileName(null);
    setWebBannerUrl(null);
  };

  const removePhoneBanner = () => {
    setPhoneBannerImage(null);
    setPhoneBannerFileName(null);
    setPhoneBannerUrl(null);
  };

  const handleMobileInterest = () => {
    setShowMobileForm(true);
  };

  const preparePayload = () => {
    const name = watch('template');

    return {
      webBanner: {
        name: webBannerFileName || '',
        url: webBannerUrl || ''
      },
      project_id: projectID,
      phoneBanner: {
        name: phoneBannerFileName || '',
        url: phoneBannerUrl || ''
      },
      name: name,
      oneLiner: userProposition,
      color: selectedColor,
      sales_ids: selectedSalesPersons.map((person) => person.id)
    };
  };

  const handleTemplateSubmit = () => {
    const payload = preparePayload();

    // Validation
    if (!payload.name || payload.name.trim() === '') {
      toast.error('Please select a template');
      return;
    }

    if (!projectID) {
      toast.error('Please select a project');
      return;
    }

    if (!userProposition.trim()) {
      toast.error('Please enter user selling proposition');
      return;
    }

    // Check if at least one sales person is selected
    if (!selectedSalesPersons || selectedSalesPersons.length === 0) {
      toast.error('Please select at least one sales person');
      return;
    }

    // Check if images are still uploading
    if (uploadingWebBanner || uploadingPhoneBanner) {
      toast.error('Please wait for images to finish uploading');
      return;
    }

    // Check if files are selected but URLs are missing (upload failed)
    if (webBannerFileName && !webBannerUrl) {
      toast.error('Web banner upload failed. Please try uploading again.');
      return;
    }

    if (phoneBannerFileName && !phoneBannerUrl) {
      toast.error('Phone banner upload failed. Please try uploading again.');
      return;
    }

    console.log('Template Payload:', payload);

    // Check if template exists to decide between create or update
    if (campaignTemplate && campaignTemplate.length > 0 && campaignTemplate[0]?.id) {
      handleUpdateTemplate(payload);
    } else {
      handleCreateTemplate(payload);
    }
  };

  const { mutate: handleCreateTemplate } = useMutation({
    mutationFn: (payload) => createCampaignTemplate(payload),
    onMutate: () => {
      setisLoading(true);
    },
    onSuccess: async (data) => {
      setisLoading(false);
      toast.success('Template created successfully!');
    },
    onError: (err) => {
      setisLoading(false);
      console.error(`Error creating new template`, err);
      toast.error(err?.response?.data?.message || err?.message || `Error creating new template`);
    }
  });

  return (
    <>
      <Loader isVisible={isloading || isProjectsLoading} />
      <Layout>
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header backArrow={false} data={{ name: 'Templates' }} />
        </div>
        <div
          className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''} h-[calc(100vh-4.4rem)]`}>
          <div className="mt-[4.4rem] h-full flex flex-col">
            {/* Header Controls */}

            {/* Main Content */}
            <div className="flex-1 flex gap-6 p-2 pt-0 overflow-hidden">
              {/* Template Preview */}
              <div className="flex-1 overflow-auto">
                <div className="bg-gray-100 rounded-lg h-full flex flex-col justify-between items-center">
                  <div className="flex justify-between items-center gap-4 px-2 flex-shrink-0 w-full">
                    <div className="flex gap-4 items-center">
                      {/* Project Navigation */}
                      <NavigationSelector
                        data={projects || []}
                        onSelectionChange={handleProjectSelect}
                        initialIndex={currentProjectIndex}
                        labelKey="name"
                        valueKey="id"
                        placeholder="Loading..."
                      />

                      <NavigationSelector
                        data={templateOptions}
                        onSelectionChange={handleTemplateSelect}
                        initialIndex={currentTemplateIndex}
                        labelKey="name"
                        valueKey="value"
                        placeholder="No templates"
                      />
                    </div>
                    <div className="flex gap-2 bg-white p-2 rounded-lg px-4">
                      <button
                        onClick={() => {
                          setShowMobileView(false);
                          setShowMobileForm(false);
                        }}
                        className={`transition-all ${!showMobileView ? '' : 'opacity-50 grayscale'}`}>
                        <img src={WebIcon} alt="web" className="w-6 h-6" />
                      </button>
                      <div className="w-px h-8 bg-gray-300 my-auto"></div>
                      <button
                        onClick={() => {
                          setShowMobileView(true);
                          setShowMobileForm(false);
                        }}
                        className={`transition-all ${showMobileView ? '' : 'opacity-50 grayscale'}`}>
                        <img src={MobileIcon} alt="mobile" className="w-6 h-6" />
                      </button>
                    </div>
                  </div>
                  {!showMobileView ? (
                    <div className="bg-white rounded-lg shadow-lg overflow-hidden w-full h-[86%]">
                      {/* Header */}

                      {/* Content Area with Background */}
                      <div
                        className="relative h-full flex items-center flex-col overflow-auto"
                        style={{
                          backgroundImage: webBannerImage
                            ? `url(${webBannerImage})`
                            : 'linear-gradient(to bottom right,  #808080,#BEBEBE)',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          backgroundRepeat: 'no-repeat'
                        }}>
                        <div className=" text-white p-4 flex items-center justify-end w-full">
                          <img src={LOGO} alt="HousinGram Logo" className="h-8" />
                        </div>
                        {/* Overlay for better text readability */}
                        {/* <div className="absolute inset-0 bg-gradient-to-r from-purple-900/40 to-blue-900/40"></div> */}
                        {/* Content */}
                        <div className="relative z-10 pl-8 flex w-full flex-col ">
                          {/* Left Content */}
                          <div className="flex-1 w-[65%]">
                            <h2
                              className={`text-4xl font-semibold mb-4 leading-tight ${
                                selectedColor === 'white' ? 'text-white' : 'text-black'
                              }`}>
                              {userProposition}
                            </h2>
                          </div>

                          {/* Right Form - Glass Effect */}
                          <div className="w-[35%] bg-white/20 backdrop-blur-md  p-6 rounded-lg shadow-xl mt-3">
                            <h3
                              className={`font-semibold mb-4 text-lg ${
                                selectedColor === 'white' ? 'text-white' : 'text-black'
                              }`}>
                              Fill Details
                            </h3>
                            <div className="space-y-4">
                              <div>
                                <label
                                  className={`block text-sm mb-1 font-medium ${
                                    selectedColor === 'white' ? 'text-white/90' : 'text-black/90'
                                  }`}>
                                  Name *
                                </label>
                                <input
                                  type="text"
                                  placeholder="Enter your name"
                                  className={`w-full p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-white/50 bg-white/20 backdrop-blur-sm  ${
                                    selectedColor === 'white'
                                      ? 'text-white placeholder-white/70'
                                      : 'text-black placeholder-black/70'
                                  }`}
                                />
                              </div>
                              <div>
                                <label
                                  className={`block text-sm mb-1 font-medium ${
                                    selectedColor === 'white' ? 'text-white/90' : 'text-black/90'
                                  }`}>
                                  Phone Number *
                                </label>
                                <input
                                  type="text"
                                  placeholder="Enter your contact number"
                                  className={`w-full p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-white/50 bg-white/20 backdrop-blur-sm  ${
                                    selectedColor === 'white'
                                      ? 'text-white placeholder-white/70'
                                      : 'text-black placeholder-black/70'
                                  }`}
                                />
                              </div>

                              <button
                                className={`w-full py-3 rounded-md font-medium transition-colors mt-4 shadow-lg ${
                                  selectedColor === 'white'
                                    ? 'bg-green-600 hover:bg-green-700 text-white'
                                    : 'bg-black hover:bg-gray-800 text-white'
                                }`}>
                                Submit
                              </button>
                            </div>
                          </div>

                          {/* Contact Info below form - outside glass background */}
                          <div
                            className={`w-[35%] flex items-center justify-start gap-4 text-sm mt-2 mb-4 ${
                              selectedColor === 'white' ? 'text-white' : 'text-black'
                            }`}>
                            <div className="flex items-center gap-2">
                              <Phone
                                className={`w-4 h-4 ${
                                  selectedColor === 'white' ? 'text-white' : 'text-black'
                                }`}
                              />
                              <span>+91-XXXXXXXXX</span>
                            </div>
                            <div
                              className={`w-px h-4 ${
                                selectedColor === 'white' ? 'bg-white/40' : 'bg-black/40'
                              }`}></div>
                            <div className="flex items-center gap-2">
                              <Mail
                                className={`w-4 h-4 ${
                                  selectedColor === 'white' ? 'text-white' : 'text-black'
                                }`}
                              />
                              <span>info@housingram.com</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Mobile Preview - Single Mobile Device
                    <div className="flex justify-center items-center w-full h-full">
                      <div className="w-64 h-[500px] rounded-lg overflow-hidden shadow-xl">
                        {/* Content */}
                        <div
                          className="h-full relative overflow-auto flex flex-col"
                          style={{
                            backgroundImage: phoneBannerImage
                              ? `url(${phoneBannerImage})`
                              : webBannerImage
                                ? `url(${webBannerImage})`
                                : 'linear-gradient(to bottom right, #808080, #BEBEBE)',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                          }}>
                          {/* Status Bar */}
                          <div
                            className={`p-2 text-center text-xs flex justify-between items-center px-4 ${
                              selectedColor === 'white' ? 'text-white' : 'text-black'
                            }`}>
                            <span>9:41</span>
                            <img src={Network} alt="HousinGram Logo" className="h-2" />
                          </div>
                          {/* Header */}
                          <div
                            className={`p-2 flex items-center justify-end w-full ${
                              selectedColor === 'white' ? 'text-white' : 'text-black'
                            }`}>
                            <img src={LOGO} alt="HousinGram Logo" className="h-6" />
                          </div>

                          {/* Content */}
                          <div className="relative z-10 p-4 flex flex-col h-full">
                            {!showMobileForm ? (
                              <>
                                <div className="flex-1">
                                  <h3
                                    className={`text-lg font-semibold mb-2 leading-tight ${
                                      selectedColor === 'white' ? 'text-white' : 'text-black'
                                    }`}>
                                    {userProposition}
                                  </h3>
                                </div>

                                <div className="">
                                  <button
                                    onClick={handleMobileInterest}
                                    className={`w-full backdrop-blur-md hover:bg-white/25 py-2 rounded-lg text-sm font-medium transition-colors shadow-lg ${
                                      selectedColor === 'white'
                                        ? 'bg-white/20 text-white'
                                        : 'bg-black/20 text-black'
                                    }`}>
                                    I&apos;m Interested
                                  </button>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="mb-4">
                                  <h3
                                    className={`text-lg font-semibold mb-2 leading-tight ${
                                      selectedColor === 'white' ? 'text-white' : 'text-black'
                                    }`}>
                                    {userProposition}
                                  </h3>
                                </div>

                                <div className="bg-white/20 backdrop-blur-md rounded-lg p-3 space-y-2">
                                  <h4
                                    className={`font-semibold text-sm mb-2 ${
                                      selectedColor === 'white' ? 'text-white' : 'text-black'
                                    }`}>
                                    Fill Details
                                  </h4>
                                  <div>
                                    <label
                                      className={`block text-xs mb-1 ${
                                        selectedColor === 'white'
                                          ? 'text-white/90'
                                          : 'text-black/90'
                                      }`}>
                                      Name *
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="Enter your name"
                                      className={`w-full p-2 rounded text-xs bg-white/20 backdrop-blur-sm  ${
                                        selectedColor === 'white'
                                          ? 'text-white placeholder-white/70'
                                          : 'text-black placeholder-black/70'
                                      }`}
                                    />
                                  </div>
                                  <div>
                                    <label
                                      className={`block text-xs mb-1 ${
                                        selectedColor === 'white'
                                          ? 'text-white/90'
                                          : 'text-black/90'
                                      }`}>
                                      Phone Number *
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="Enter your contact number"
                                      className={`w-full p-2 rounded text-xs bg-white/20 backdrop-blur-sm  ${
                                        selectedColor === 'white'
                                          ? 'text-white placeholder-white/70'
                                          : 'text-black placeholder-black/70'
                                      }`}
                                    />
                                  </div>
                                  <button
                                    className={`w-full py-2 rounded text-xs font-medium mt-2 transition-colors ${
                                      selectedColor === 'white'
                                        ? 'bg-green-600 hover:bg-green-700 text-white'
                                        : 'bg-black hover:bg-gray-800 text-white'
                                    }`}>
                                    Submit
                                  </button>
                                </div>
                              </>
                            )}
                          </div>

                          {/* Contact Info - Always at bottom */}
                          <div
                            className={`flex items-center justify-center gap-2 p-4 mt-auto ${
                              selectedColor === 'white' ? 'text-white' : 'text-black'
                            }`}>
                            <div className="flex items-center gap-1">
                              <Phone
                                className={`w-3 h-3 ${
                                  selectedColor === 'white' ? 'text-white' : 'text-black'
                                }`}
                              />
                              <span className="text-[9px]">+91-XXXXXXXXX</span>
                            </div>
                            <div
                              className={`w-px h-3 ${
                                selectedColor === 'white' ? 'bg-white/40' : 'bg-black/40'
                              }`}></div>
                            <div className="flex items-center gap-1">
                              <Mail
                                className={`w-3 h-3 ${
                                  selectedColor === 'white' ? 'text-white' : 'text-black'
                                }`}
                              />
                              <span className="text-[9px]">info@housingram.com</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Edit Template Panel */}
              <div className="w-80 bg-white border rounded-lg p-6 h-full flex flex-col">
                <h3 className="text-lg font-semibold mb-6 flex-shrink-0">Edit Template</h3>

                {/* Scrollable Form Content */}
                <div className="flex-1 overflow-y-auto pr-2">
                  {/* Upload Web Banner */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload web banner image
                    </label>
                    {webBannerFileName ? (
                      <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-700 truncate">
                              {webBannerFileName}
                            </span>
                            {uploadingWebBanner && (
                              <div className="flex items-center gap-1">
                                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                <span className="text-xs text-blue-600">Uploading...</span>
                              </div>
                            )}
                          </div>
                          <button
                            onClick={removeWebBanner}
                            className="text-red-500 hover:text-red-700 p-1"
                            title="Remove image"
                            disabled={uploadingWebBanner}>
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 text-center">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleWebBannerUpload}
                          className="hidden"
                          id="web-banner-upload"
                          disabled={uploadingWebBanner}
                        />
                        <label
                          htmlFor="web-banner-upload"
                          className={`cursor-pointer ${uploadingWebBanner ? 'opacity-50' : ''}`}>
                          <Upload className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                          <span className="text-sm text-gray-500">
                            {uploadingWebBanner ? 'Uploading...' : 'Select from files'}
                          </span>
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Upload Phone Banner */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload phone banner image
                    </label>
                    {phoneBannerFileName ? (
                      <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-700 truncate">
                              {phoneBannerFileName}
                            </span>
                            {uploadingPhoneBanner && (
                              <div className="flex items-center gap-1">
                                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                <span className="text-xs text-blue-600">Uploading...</span>
                              </div>
                            )}
                          </div>
                          <button
                            onClick={removePhoneBanner}
                            className="text-red-500 hover:text-red-700 p-1"
                            title="Remove image"
                            disabled={uploadingPhoneBanner}>
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 text-center">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhoneBannerUpload}
                          className="hidden"
                          id="phone-banner-upload"
                          disabled={uploadingPhoneBanner}
                        />
                        <label
                          htmlFor="phone-banner-upload"
                          className={`cursor-pointer ${uploadingPhoneBanner ? 'opacity-50' : ''}`}>
                          <Upload className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                          <span className="text-sm text-gray-500">
                            {uploadingPhoneBanner ? 'Uploading...' : 'Select from files'}
                          </span>
                        </label>
                      </div>
                    )}
                  </div>

                  {/* User Selling Proposition */}
                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        User selling proposition (one liner)
                      </label>
                      <div className="flex gap-2">
                        <div
                          onClick={() => setSelectedColor('white')}
                          className={`w-5 h-5 rounded-full bg-white border-2 cursor-pointer transition-all ${
                            selectedColor === 'white'
                              ? 'border-blue-500 scale-110'
                              : 'border-gray-300'
                          }`}
                          title="White theme"
                        />
                        <div
                          onClick={() => setSelectedColor('black')}
                          className={`w-5 h-5 rounded-full bg-black border-2 cursor-pointer transition-all ${
                            selectedColor === 'black'
                              ? 'border-blue-500 scale-110'
                              : 'border-gray-300'
                          }`}
                          title="Black theme"
                        />
                      </div>
                    </div>
                    <textarea
                      value={userProposition}
                      onChange={(e) => setUserProposition(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows="3"
                      placeholder="Enter the text within 6-8 words..."
                    />
                  </div>

                  {/* Sales Persons Selector */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assign Sales Persons
                    </label>
                    <Controller
                      name="sales_ids"
                      control={control}
                      render={({ field }) => (
                        <Multiselect
                          options={salesPersons?.data?.filter((item) => item.level === 'Low') || []}
                          selectedValues={selectedSalesPersons}
                          onSelect={(selectedList) => setSelectedSalesPersons(selectedList)}
                          onRemove={(selectedList) => setSelectedSalesPersons(selectedList)}
                          displayValue="name"
                          placeholder="Select sales persons"
                          style={{
                            chips: {
                              background: '#2A565E',
                              color: 'white'
                            },
                            searchBox: {
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              padding: '8px',
                              background: 'white',
                              color: '#333'
                            },
                            input: {
                              color: '#333'
                            },
                            singleValue: {
                              color: '#333'
                            },
                            placeholder: {
                              color: '#6c757d'
                            },
                            option: {
                              color: '#333',
                              background: 'white'
                            },
                            listItem: {
                              color: '#333',
                              background: 'white'
                            }
                          }}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Submit Button - Fixed at bottom */}
                <div className="flex-shrink-0 mt-6">
                  <Button
                    type="button"
                    displayType={'full-w-primary'}
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    onClick={handleTemplateSubmit}
                    disabled={uploadingWebBanner || uploadingPhoneBanner || isloading}>
                    {uploadingWebBanner || uploadingPhoneBanner
                      ? 'Uploading Images...'
                      : isloading
                        ? 'Loading...'
                        : campaignTemplate && campaignTemplate.length > 0
                          ? 'Update'
                          : 'Submit'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default CampaignTemplates;
